import React, { Component } from "react";
import * as THREE from "three";
import OrbitControls from "three-orbitcontrols";
import LCFragmentShader from "../shaders/LC-fragment.glsl.js";
import LCVertexShader from "../shaders/LC-vertex.glsl.js";
import GPUComputationRenderer from "../lib/GPUComputationRenderer";
import LSystem from "../lib/LSystem";

const width = 1024,
  height = 1024;

class CurveGeometry extends THREE.BufferGeometry {
  constructor(width, height) {
    super();
    const points = width * height;

    const vertices = new THREE.BufferAttribute(
      new Float32Array(points * 6 * 3),
      3
    );
    const references = new THREE.BufferAttribute(
      new Float32Array(points * 6 * 2),
      2
    );
    this.addAttribute("position", vertices);
    this.addAttribute("reference", references);

    const verts_push = (() => {
      let v = 0;
      return (...args) => {
        for (var i = 0; i < args.length; i++) {
          vertices.array[v++] = args[i];
        }
      };
    })();

    for (let i = 0; i < points; ++i) {
      verts_push(-1, -1, 0, 1, -1, 0, 1, 1, 0);
      verts_push(1, 1, 0, -1, -1, 0, -1, 1, 0);
    }

    let v = 0;
    for (var i = 0; i < points; i++) {
      var x = (i % width) / width;
      var y = Math.floor(i / width) / height;
      for (let j = 0; j < 6; ++j) {
        references.array[v++] = x;
        references.array[v++] = y;
      }
    }
  }
}

class Curve {
  constructor() {
    this.geometry = new CurveGeometry(width, height);

    // ShaderMaterial
    this.material = new THREE.ShaderMaterial({
      uniforms: (this.uniforms = {
        curveTexture: {
          value: null
        },
        zoom: { value: 1 },
        size: { value: Math.min(window.innerWidth, window.innerHeight) },
        width: { value: width },
        height: { value: height }
      }),
      vertexShader: LCVertexShader,
      fragmentShader: LCFragmentShader,
      side: THREE.DoubleSide
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
  }

  init(renderer, lSystem) {
    const gpu = (this.gpu = new GPUComputationRenderer(
      width,
      height,
      renderer
    ));
    var curveTexture = gpu.createTexture();

    this.variable = gpu.addVariable(
      "curveTexture",
      lSystem.getShader(),
      curveTexture
    );
    gpu.setVariableDependencies(this.variable, []);
    const uniforms = this.variable.material.uniforms;
    uniforms.width = {
      value: width
    };
    uniforms.height = {
      value: height
    };
    const error = gpu.init();
    if (error !== null) {
      console.error(error);
    }
    gpu.compute();
  }
}

class LCanvas extends Component {
  static defaultProps = {
    lSystem: LSystem.Sierpinski(13)
  };

  constructor() {
    super();
    this.enabled = true;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    camera.position.set(0, 20, 0);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer();
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.mouseButtons = {
      RIGHT: THREE.MOUSE.LEFT,
      MIDDLE: THREE.MOUSE.MIDDLE,
      LEFT: THREE.MOUSE.RIGHT
    };
    controls.target.set(0, 0, 0); // view direction perpendicular to XY-plane
    controls.enableRotate = false;
    controls.enableZoom = true;
    //controls.minZoom = 1; // optional

    controls.update();

    const curve = new Curve();
    scene.add(curve.mesh);

    this.onWindowResize = () => this.updateSize();
    window.addEventListener("resize", this.onWindowResize);
    this.camera = camera;
    this.curve = curve;
    this.renderer = renderer;
    this.controls = controls;
    this.scene = scene;
    this.updateSize();
    requestAnimationFrame(() => this.animate());
  }

  updateSize() {
    const w = window.innerWidth,
      h = window.innerHeight;
    const s = Math.min(w, h);
    this.camera.left = (-1 * w) / s;
    this.camera.right = (1 * w) / s;
    this.camera.bottom = (-1 * h) / s;
    this.camera.top = (1 * h) / s;
    this.curve.uniforms.size.value = s;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  setLSystem(lSystem) {
    if (this.lSystem !== lSystem) {
      this.lSystem = lSystem;
      this.curve.init(this.renderer, lSystem);
    }
  }

  render() {
    this.setLSystem(this.props.lSystem);
    return (
      <div
        className="l-canvas"
        ref={d => {
          if (d) d.appendChild(this.renderer.domElement);
        }}
      />
    );
  }

  animate() {
    if (this.enabled) {
      requestAnimationFrame(() => this.animate());

      if (this.curve.gpu) {
        this.curve.uniforms.zoom.value = this.controls.object.zoom;
        this.curve.uniforms.curveTexture.value = this.curve.gpu.getCurrentRenderTarget(
          this.curve.variable
        ).texture;
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
      }
    }
  }

  componentWillUnmount() {
    window.removeListener("resize", this.onWindowResize);
    this.enabled = false;
  }
}

export default LCanvas;
